import * as React from "react";
import { Typography, Box, Button, Grid, Dialog, DialogContent, useMediaQuery, IconButton, Icon, Stepper, Step, StepLabel, MenuItem } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@mui/styles";
import CloseModalIcon from "../../icons/RoundCloseIcon.svg";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { getCreateBankAccount, selectPaymentState, getPaymentMethods, clearBankAndCardStates } from "../../resui/payments/sagas/residentpaymentsSlice";
import SpinnerModal from "./SpinnerModal";
import { approvedISOStandards } from "../util/approvedISOStandards";
import { setCreateBankAccountRequest } from "../../pmui/residents/saga/pmResidentsSlice";
import { usePmActor } from "../login/usePmActor";
import { defaultUIContext, UIContextEnum } from "../../pmui/residents/MakeAdhocOtp";
import RoundBackIcon from "../../icons/RoundBackIcon.svg";
import { useFormik } from "formik";
import { schemaValidationBankDetailsType, schemaValidationBankDetails, schemaValidationBankAccountOwnershipType, schemaValidationBankAccountOwnership } from "../../utils/validation";
import InputControllerBank from "../../components/Form/InputControllerBank";
import BankOwnershipIcon from "../../icons/BankOwnershipIcon.svg";
import BankConfirmIcon from "../../icons/BankConfirmIcon.svg";
import BankConfirmIconAlt from "../../icons/BankConfirmIconAlt.svg";
import CustomDropdown from "../../components/customDropdown";
import CheckBox from "../../icons/CheckBox.svg";
import UnCheckedBox from "../../icons/UnCheckedBox.svg";
import { selectAuthState } from "../../shared/sagas/auth/authSlice";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    margin: "1px",
    position: "relative",
  },
  stepperContainer: {
    paddingTop: "50px",
    width: "100%",
  },
  stepper: {
    width: "100%",
    "& .MuiStepLabel-root": {
      display: "flex",
      flexDirection: "column-reverse !important",
      marginTop: "-65px",
    },
    "& .MuiStepLabel-label": {
      display: "flex",
      justifyContent: "center",
    },
    "& .MuiStepLabel-labelContainer": {
      marginBottom: "9px",
    },
    "& .MuiStepConnector-root": {
      top: "15px",
    },
    "& .MuiStepIcon-root": {
      color: "#F1F0F5",
      width: "29px",
      height: "29px",
    },
    "& .MuiStepIcon-root.Mui-completed": {
      color: "#39DB8D",
    },
    "& .MuiStepIcon-root .MuiStepIcon-text": {
      fill: "#14142B",
      fontWeight: "600",
    },
    "& .MuiStepIcon-root.Mui-active": {
      color: "#39DB8D",
    },
    "& .MuiStepIcon-root.Mui-active .MuiStepIcon-text": {
      fill: "#fff",
    },
  },
  stepperText: {
    fontFamily: "Work Sans !important",
    fontStyle: "normal",
    fontWeight: "400",
    fontSize: "18px !important",
    lineHeight: "21px !important",
    maxWidth: "100px",
    color: "#14142B",
  },
  modalText: {
    fontFamily: "Work Sans !important",
    fontStyle: "normal !important",
    fontWeight: "400 !important",
    fontSize: "14px !important",
    lineHeight: "22px !important",
    color: "#000000 !important",
    textAlign: "center",
  },
  previewDetailText1: {
    fontFamily: "Work Sans !important",
    fontStyle: "normal !important",
    fontWeight: "600 !important",
    fontSize: "10px !important",
    lineHeight: "13px !important",
    color: "#000000 !important",
    maxWidth: "70% !important",
    whiteSpace: "nowrap",
    overflow: "hidden !important",
    textOverflow: "ellipsis !important",
  },
  previewDetailText2: {
    fontFamily: "Work Sans !important",
    fontStyle: "normal !important",
    fontWeight: "600 !important",
    fontSize: "9px !important",
    lineHeight: "22px !important",
    color: "#000000 !important",
  },
  previewDetailText3: {
    fontFamily: "Work Sans !important",
    fontStyle: "normal !important",
    fontWeight: "400 !important",
    fontSize: "13px !important",
    lineHeight: "21px !important",
    color: "#000000 !important",
  },
  accpetText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: 400,
    fontSize: '16px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
    marginLeft: "10px !important",
    cursor: "pointer !important",
  },
}));

interface NewBankAccountProps {
  openBankModal: boolean;
  handleCloseBankModal: () => void;
  handleCloseGoBackBankModal: () => void;
  smallVersion?: boolean;
}

const steps = ["Bank Details", "Account Ownership", "Confirm Details"];

export default function AddNewBankPaymentMethod(props: NewBankAccountProps) {
  const currentUI = React.useContext(defaultUIContext);
  const [isPmActor] = usePmActor();
  const classes = useStyles();
  const dispatch = useDispatch();
  const isOnMobileScreen = useMediaQuery("(max-width:600px)");
  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;

  const [activeStep, setActiveStep] = React.useState(0);
  const [bankGlobalError, setBankGlobalError] = React.useState<string | null>(null);
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const { isBankAddedSuccess, isError, bankErrorMessage, isFetching } = useSelector(selectPaymentState);
  const { ipaUser } = useSelector(selectAuthState);

  const bankDetailsInitialValues: schemaValidationBankDetailsType = {
    NickName: "",
    BankName: "",
    AccountType: "C",
    RoutingNumber: "",
    ConfirmRoutingNumber: "",
    AccountNumber: "",
    ConfirmAccountNumber: ""
  }
  const detailsFormik = useFormik<schemaValidationBankDetailsType>({
    initialValues: bankDetailsInitialValues,
    validationSchema: schemaValidationBankDetails,
    onSubmit: async (values) => {
      setActiveStep(1);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const bankOwnershipInitialValues: schemaValidationBankAccountOwnershipType = {
    BankHolderName: "",
    City: "",
    State: approvedStates[0].Code,
    Zip: "",
    Line1: "",
    Country: "US",
  }
  const ownershipFormik = useFormik<schemaValidationBankAccountOwnershipType>({
    initialValues: bankOwnershipInitialValues,
    validationSchema: schemaValidationBankAccountOwnership,
    onSubmit: async (values) => {
      setActiveStep(2);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const [alignment, setAlignment] = React.useState("checking");
  const handleChangeAlignment = (event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
    setAlignment(newAlignment);
  };

  const [isFutureUse, setIsFutureUse] = React.useState(false);
  const isAdhocUI = isPmActor || currentUI === UIContextEnum.pmUI;

  const handleAddBankSubmit = () => {
    const names = ownershipFormik.values.BankHolderName.split(" ");

    setBankGlobalError(null);
    dispatch({ type: clearBankAndCardStates.type });

    if (Boolean(detailsFormik.isValid && ownershipFormik.isValid)) {
      !isAdhocUI && setIsSpinnerModalOpen(true);
      dispatch({
        type: isAdhocUI ? setCreateBankAccountRequest.type : getCreateBankAccount.type,
        payload: {
          //Savings: S, checkings: C
          AccountType: detailsFormik.values.AccountType,
          AccountNumber: detailsFormik.values.AccountNumber,
          RoutingNumber: detailsFormik.values.RoutingNumber,
          BillingAddress: {
            City: ownershipFormik.values.City,
            State: ownershipFormik.values.State,
            Zip: ownershipFormik.values.Zip,
            Line1: ownershipFormik.values.Line1,
            Country: ownershipFormik.values.Country,
          },
          AccountHolderFirstname: names[0],
          AccountHolderLastname: names[1],
          NickName: detailsFormik.values.NickName ?? null,
          BankName: detailsFormik.values.BankName,
          IsNotAdhoc: isFutureUse,
        },
      });

      // skip spinner for adhoc otp and close modal
      isAdhocUI && props.handleCloseBankModal();
    }
  };

  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
    !isAdhocUI && dispatch(getPaymentMethods()); // do nothing if pm is actor
  }, [dispatch, getPaymentMethods]);

  React.useEffect(() => {
    if (isBankAddedSuccess) {
      setSpinnerStatus("stop");
    }

    if (isError) {
      setIsSpinnerModalOpen(false);
      setBankGlobalError(bankErrorMessage);
      dispatch({ type: clearBankAndCardStates.type });
    }
  }, [isBankAddedSuccess, isError, bankErrorMessage]);

  const handleGoBack = () => {
    setBankGlobalError(null);
    if (activeStep === 0) {
      props.handleCloseGoBackBankModal();
    } else if (activeStep === 1) {
      setActiveStep(0);
    } else if (activeStep === 2) {
      setActiveStep(1);
    }
  }

  const [selectedState, setSelectedState] = React.useState(approvedStates[0].Code);
  const resetStateSelection = () => {
    ownershipFormik.setFieldValue("State", "");
    setSelectedState("");
  };

  React.useEffect(() => {
    ownershipFormik.values.Country && ["US", "CA"].includes(ownershipFormik.values.Country) && approvedStates.map((stateRecord) => stateRecord.Code).includes(ownershipFormik.values.State) && setSelectedState(ownershipFormik.values.State);
  }, [ownershipFormik]);

  const customDropdownStyle = {
    paddingLeft: "5px",
    borderRadius: "24px",
    height: "49px",
    backgroundColor: "white",
    width: '100%',
    fontFamily: "Work Sans",
  }

  return (
    <React.Fragment>
      <Dialog
        open={props.openBankModal}
        onClose={props.handleCloseBankModal}
        fullScreen={isOnMobileScreen}
        fullWidth={true}
        disableEnforceFocus
        PaperProps={{
          style: {
            pointerEvents: "auto",
            maxWidth: isOnMobileScreen ? "100%" : props.smallVersion ? "472px" : "600px",
            minHeight: isOnMobileScreen ? "100%" : props.smallVersion ? "523px" : "700px",
            borderRadius: isOnMobileScreen ? "none" :"24px",
            padding: props.smallVersion ? "0px" : "0px 10px 20px",
          },
        }}
      >
        <DialogContent sx={{ padding: props.smallVersion ? "20px 24px" : "40px 30px" }}>
          <Box sx={{ position: "relative" }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', minHeight: 50 }}>
              <Box sx={{ position: 'absolute', left: 0, top: 0 }}>
                <IconButton id="goBackBankModalBtn" sx={{ ml: "-8px"  }} onClick={handleGoBack}>
                  <Icon sx={{ width: 26, height: 26 }}>
                    <img src={RoundBackIcon} alt="Back" width={26} height={26} />
                  </Icon>
                </IconButton>
              </Box>
              <Box className={classes.stepperContainer} sx={{ display: isOnMobileScreen ? "none" : "block", mx: 4 }}>
                <Stepper alternativeLabel activeStep={activeStep} classes={{ root: classes.stepper }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel sx={{ display: "flex", justifyContent: "center" }}>
                        <Typography className={classes.stepperText}>{label}</Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
              <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
                <IconButton sx={{ mr: "-8px"  }} onClick={() => props.handleCloseBankModal()}>
                  <Icon sx={{ width: 26, height: 26 }}>
                    <img src={CloseModalIcon} alt="Close" width={26} height={26} />
                  </Icon>
                </IconButton>
              </Box>
            </Box>

            {activeStep === 0 ? (
              <Box component="form" onSubmit={detailsFormik.handleSubmit} noValidate>
                <Grid container spacing={2} sx={{ mt: 8 }}>
                  <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"NickName"} 
                      label={"Give this account a name"} 
                      value={detailsFormik.values.NickName ?? ""}
                      errors={detailsFormik.errors?.NickName}
                      touched={detailsFormik.touched?.NickName}
                      onBlur={detailsFormik.handleBlur}
                      onChange={(e) => detailsFormik.setFieldValue("NickName", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"BankName"} 
                      label={"Bank Name"} 
                      value={detailsFormik.values.BankName ?? ""}
                      errors={detailsFormik.errors?.BankName}
                      touched={detailsFormik.touched?.BankName}
                      onBlur={detailsFormik.handleBlur}
                      onChange={(e) => detailsFormik.setFieldValue("BankName", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important", marginBottom: "12px" }}>
                    <ToggleButtonGroup color="primary" value={alignment} exclusive onChange={handleChangeAlignment} style={{ width: "100%" }}>
                      <ToggleButton
                        value="checking"
                        style={{
                          fontFamily: "'Work Sans', sans-serif",
                          width: "50%",
                          border: "1px solid #39A2DB",
                          borderRadius: "24px 0 0 24px ",
                          background: detailsFormik.values.AccountType === 'C' ? "#39A2DB" : "#fff",
                          color: detailsFormik.values.AccountType === 'C' ? "#fff" : "#39A2DB",
                          textTransform: "capitalize",
                          fontSize: "12px",
                          lineHeight: "22px",
                          padding: "5px 10px",
                          height: "49px",
                        }}
                        size="small"
                        onClick={() => detailsFormik.setFieldValue("AccountType", "C")}
                      >
                        Checking
                      </ToggleButton>
                      <ToggleButton
                        value="savings"
                        style={{
                          width: "50%",
                          fontFamily: "'Work Sans', sans-serif",
                          border: "1px solid #39A2DB",
                          borderRadius: "0 24px 24px 0 ",
                          background: detailsFormik.values.AccountType === 'S' ? "#39A2DB" : "#fff",
                          color: detailsFormik.values.AccountType === 'S' ? "#fff" : "#39A2DB",
                          textTransform: "capitalize",
                          fontSize: "12px",
                          lineHeight: "22px",
                          padding: "5px 10px",
                          height: "49px",
                        }}
                        size="small"
                        onClick={() => detailsFormik.setFieldValue("AccountType", "S")}
                      >
                        Savings
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"AccountNumber"} 
                      label={"Account Number"} 
                      value={detailsFormik.values.AccountNumber ?? ""}
                      errors={detailsFormik.errors?.AccountNumber}
                      touched={detailsFormik.touched?.AccountNumber}
                      onBlur={detailsFormik.handleBlur}
                      onChange={(e) => detailsFormik.setFieldValue("AccountNumber", e.target.value)}
                      isfullWidth={true}
                      isDisabledCopy={true}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"ConfirmAccountNumber"} 
                      label={"Confirm Account Number"} 
                      value={detailsFormik.values.ConfirmAccountNumber ?? ""}
                      errors={detailsFormik.errors?.ConfirmAccountNumber}
                      touched={detailsFormik.touched?.ConfirmAccountNumber}
                      onBlur={detailsFormik.handleBlur}
                      onChange={(e) => detailsFormik.setFieldValue("ConfirmAccountNumber", e.target.value)}
                      isfullWidth={true}
                      isDisabledCopy={true}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"RoutingNumber"} 
                      label={"Routing Number"} 
                      value={detailsFormik.values.RoutingNumber ?? ""}
                      errors={detailsFormik.errors?.RoutingNumber}
                      touched={detailsFormik.touched?.RoutingNumber}
                      onBlur={detailsFormik.handleBlur}
                      onChange={(e) => detailsFormik.setFieldValue("RoutingNumber", e.target.value)}
                      isfullWidth={true}
                      isDisabledCopy={true}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"ConfirmRoutingNumber"} 
                      label={"Confirm Routing Number"} 
                      value={detailsFormik.values.ConfirmRoutingNumber ?? ""}
                      errors={detailsFormik.errors?.ConfirmRoutingNumber}
                      touched={detailsFormik.touched?.ConfirmRoutingNumber}
                      onBlur={detailsFormik.handleBlur}
                      onChange={(e) => detailsFormik.setFieldValue("ConfirmRoutingNumber", e.target.value)}
                      isfullWidth={true}
                      isDisabledCopy={true}
                    />
                  </Grid>
                </Grid>

                <Typography className={classes.modalText} style={{ marginTop: "10px", marginBottom: "60px" }}>
                  You can find this information in your online bank portal.
                </Typography>

                <div style={{ height: 42, width: "100%" }}></div>

                <Box sx={{ position: "absolute", bottom: 0, right: 0 }}>
                  <Button
                    disabled={Boolean(!detailsFormik.isValid)}
                    style={{
                      width: "99px",
                      fontFamily: "'Work Sans', sans-serif",
                      borderRadius: "24px",
                      backgroundColor: Boolean(!detailsFormik.isValid) ? "#D3D3D3" : "#39A2DB",
                      height: "33px",
                      textTransform: "capitalize",
                      fontSize: "18px",
                      color: "#fff",
                    }}
                    size="small"
                    type="submit"
                    id="continueNewBankBtn1"
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            ) : activeStep === 1 ? (
              <Box component="form" onSubmit={ownershipFormik.handleSubmit} noValidate>
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <img src={BankOwnershipIcon} alt="Back" width={props.smallVersion || isOnMobileScreen ? 319: 400} height={props.smallVersion || isOnMobileScreen ? 131: 164} />
                </Box>
                <Typography className={classes.modalText} sx={{ mb: 3, mt: 1 }}>
                  This information would be found on the top of your check.
                </Typography>
                <Grid container spacing={2} sx={{ marginBottom: "30px" }}>
                  <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"BankHolderName"} 
                      label={"Name of Account Holder"} 
                      value={ownershipFormik.values.BankHolderName ?? ""}
                      errors={ownershipFormik.errors?.BankHolderName}
                      touched={ownershipFormik.touched?.BankHolderName}
                      onBlur={ownershipFormik.handleBlur}
                      onChange={(e) => ownershipFormik.setFieldValue("BankHolderName", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"Line1"} 
                      label={"Address"} 
                      value={ownershipFormik.values.Line1 ?? ""}
                      errors={ownershipFormik.errors?.Line1}
                      touched={ownershipFormik.touched?.Line1}
                      onBlur={ownershipFormik.handleBlur}
                      onChange={(e) => ownershipFormik.setFieldValue("Line1", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"City"} 
                      label={"City"} 
                      value={ownershipFormik.values.City ?? ""}
                      errors={ownershipFormik.errors?.City}
                      touched={ownershipFormik.touched?.City}
                      onBlur={ownershipFormik.handleBlur}
                      onChange={(e) => ownershipFormik.setFieldValue("City", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    {ownershipFormik.values.Country && ["US", "CA"].includes(ownershipFormik.values.Country) ? (
                      <CustomDropdown
                        id={"state-dropdown"}
                        isFullWidth
                        label={"State"}
                        labelId={""}
                        value={selectedState}
                        handleChange={(e) => {
                          setSelectedState(e.target?.value);
                          ownershipFormik.setFieldValue("State", e.target?.value);
                        }}
                        customStyle={customDropdownStyle}
                      >
                        {ownershipFormik.values.Country === "US" ? 
                          approvedStates.map((stateRecord) => (
                            <MenuItem style={{ height: "36px" }} key={stateRecord.Code} value={stateRecord.Code}>
                              {stateRecord.State + " - " + stateRecord.Code}
                            </MenuItem>
                          )) : approvedProvinces.map((provinceRecord) => (
                            <MenuItem style={{ height: "36px" }} key={provinceRecord.Code} value={provinceRecord.Code}>
                              {provinceRecord.Province + " - " + provinceRecord.Code}
                            </MenuItem>
                          ))
                        }{" "}
                      </CustomDropdown>
                    ) : (
                      <InputControllerBank 
                        name={"State"} 
                        label={"State"} 
                        value={ownershipFormik.values.State ?? ""}
                        errors={ownershipFormik.errors?.State}
                        touched={ownershipFormik.touched?.State}
                        onBlur={ownershipFormik.handleBlur}
                        onChange={(e) => ownershipFormik.setFieldValue("State", e.target.value)}
                        isfullWidth={true} 
                      />
                    )}
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <InputControllerBank 
                      name={"Zip"} 
                      label={"Zip"} 
                      value={ownershipFormik.values.Zip ?? ""}
                      errors={ownershipFormik.errors?.Zip}
                      touched={ownershipFormik.touched?.Zip}
                      onBlur={ownershipFormik.handleBlur}
                      onChange={(e) => ownershipFormik.setFieldValue("Zip", e.target.value)}
                      isfullWidth={true} 
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                    <CustomDropdown
                      label={"Country"}
                      isFullWidth
                      handleChange={(e: any) => {
                        resetStateSelection();
                        // update selected country
                        ownershipFormik.setFieldValue("Country", e.target?.value);
                      }}
                      value={ownershipFormik.values.Country || ""}
                      customStyle={customDropdownStyle}
                    >
                      {approvedCountries.map((countryRecord) => (
                        <MenuItem style={{ height: "36px" }} key={countryRecord.Code} value={countryRecord.Code}>
                          {countryRecord.Country + " - " + countryRecord.Code}
                        </MenuItem>
                      ))}
                    </CustomDropdown>
                  </Grid>
                </Grid>

                <div style={{ height: 42, width: "100%" }}></div>

                <Box sx={{ position: "absolute", bottom: 0, right: 0 }}>
                  <Button
                    disabled={Boolean(!ownershipFormik.isValid)}
                    style={{
                      width: "99px",
                      fontFamily: "'Work Sans', sans-serif",
                      borderRadius: "24px",
                      backgroundColor: Boolean(!ownershipFormik.isValid) ? "#D3D3D3" : "#39A2DB",
                      height: "33px",
                      textTransform: "capitalize",
                      fontSize: "18px",
                      color: "#fff",
                    }}
                    size="small"
                    type="submit"
                    id="continueNewBankBtn2"
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                {bankGlobalError?.length ? (
                  <Typography
                    style={{
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: "normal",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "18px",
                      color: "#DB3939",
                      textAlign: "center",
                      borderTop: "2px  solid #DB3939",
                      borderRadius: "24px",
                      padding: "12px 20px",
                      margin: "20px auto 0px",
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {bankGlobalError}
                  </Typography>
                ) : (
                  ""
                )}

                <Box sx={{ 
                  mt: 2,
                  backgroundImage: `url("${props.smallVersion || isOnMobileScreen ? BankConfirmIcon : BankConfirmIconAlt}")`,
                  backgroundRepeat: "no-repeat",
                  width: props.smallVersion || isOnMobileScreen ? 319: 400,
                  height: 155,
                  padding: props.smallVersion || isOnMobileScreen ? "18px 40px 20px 22px" : "18px 50px 20px 26px",
                }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography className={classes.previewDetailText1} sx={{ maxWidth: "55% !important", fontSize: props.smallVersion || isOnMobileScreen ? "10px" : "12px !important" }}>
                      {ownershipFormik.values.BankHolderName}
                    </Typography>
                    <Typography className={classes.previewDetailText1} sx={{ maxWidth: "40% !important", fontSize: props.smallVersion || isOnMobileScreen ? "10px" : "12px !important" }}>
                      {detailsFormik.values.BankName}
                    </Typography>
                  </Box>
                  <Typography className={classes.previewDetailText1} sx={{ mt: 0.5, fontSize: props.smallVersion || isOnMobileScreen ? "10px" : "12px !important" }}>
                    {ownershipFormik.values.Line1}
                  </Typography>
                  <Typography className={classes.previewDetailText1} sx={{ mt: 0.5, fontSize: props.smallVersion || isOnMobileScreen ? "10px" : "12px !important" }}>
                    {ownershipFormik.values.City}, {ownershipFormik.values.State} {ownershipFormik.values.Zip}
                  </Typography>
                  <Box sx={{ display: "flex", mt: props.smallVersion || isOnMobileScreen ? 5 : 4.5 }}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography className={classes.previewDetailText2} sx={{ mb: -1, fontSize: props.smallVersion || isOnMobileScreen ? "9px" : "11px !important" }}>
                        Routing Number
                      </Typography>
                      <Typography className={classes.previewDetailText3} sx={{ fontSize: props.smallVersion || isOnMobileScreen ? "13px" : "15px !important", lineHeight: props.smallVersion || isOnMobileScreen ? "21px" : "26px !important" }}>
                        {detailsFormik.values.RoutingNumber}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", marginLeft: "42px" }}>
                      <Typography className={classes.previewDetailText2} sx={{ mb: -1, fontSize: props.smallVersion || isOnMobileScreen ? "9px" : "11px !important" }}>
                        Account Number
                      </Typography>
                      <Typography className={classes.previewDetailText3} sx={{ fontSize: props.smallVersion || isOnMobileScreen ? "13px" : "15px !important", lineHeight: props.smallVersion || isOnMobileScreen ? "21px" : "26px !important" }}>
                        {detailsFormik.values.AccountNumber}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Typography className={classes.modalText} sx={{ mt: 20 }}>
                  Please review the information above before proceeding.
                </Typography>

                {ipaUser?.currentProfile.isActing && (
                  <Box sx={{ display: 'flex', alignItems: 'center', padding: "20px 20px 0", width: "100%" }}>
                    <img src={isFutureUse ? CheckBox : UnCheckedBox} style={{ width: "11px", height: "11px", cursor: "pointer" }} alt='Checkmark' onClick={() => setIsFutureUse(!isFutureUse)} />
                    <Typography className={classes.accpetText} variant="caption" onClick={() => setIsFutureUse(!isFutureUse)}>
                      Add to tenant wallet
                    </Typography>
                  </Box>                  
                )}

                <div style={{ height: 52, width: "100%" }}></div>

                <Box sx={{ position: "absolute", bottom: 0, right: 0 }}>
                  <Button
                    disabled={Boolean(!detailsFormik.isValid || !ownershipFormik.isValid || isFetching)}
                    style={{
                      width: "99px",
                      fontFamily: "'Work Sans', sans-serif",
                      borderRadius: "24px",
                      backgroundColor: Boolean(!detailsFormik.isValid || !ownershipFormik.isValid) ? "#D3D3D3" : "#39A2DB",
                      height: "33px",
                      textTransform: "capitalize",
                      fontSize: "18px",
                      color: "#fff",
                    }}
                    size="small"
                    onClick={handleAddBankSubmit}
                    className={isFetching ? '' : 'active'}//it's for e2e test
                    id="confirmNewBankBtn"
                  >
                    Confirm
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      {isSpinnerModalOpen ? <SpinnerModal isInvisible={true} width={props.smallVersion ? "472px" : "600px"} height={isOnMobileScreen ? "100%" : props.smallVersion ? "523px" : "700px"} openSpinnerModal={isSpinnerModalOpen} handleCloseSpinnerModal={handleCloseSpinnerModal} spinnerStatus={spinnerStatus} closeCancel={props.handleCloseBankModal} /> : <></>}
    </React.Fragment>
  );
}
